import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LineChart, Line } from 'recharts';
import Icon from '../../../components/AppIcon';

const TreatmentEffectivenessVisualization = ({ dateRange, provider, treatmentType }) => {
  const [viewMode, setViewMode] = useState('procedure');

  const procedureEffectiveness = [
    { procedure: 'Preventive', successRate: 98.5, avgDuration: 35, patientCount: 245 },
    { procedure: 'Restorative', successRate: 96.8, avgDuration: 65, patientCount: 189 },
    { procedure: 'Endodontic', successRate: 94.2, avgDuration: 95, patientCount: 112 },
    { procedure: 'Periodontal', successRate: 91.5, avgDuration: 75, patientCount: 156 },
    { procedure: 'Prosthodontic', successRate: 95.3, avgDuration: 120, patientCount: 87 },
    { procedure: 'Oral Surgery', successRate: 93.7, avgDuration: 85, patientCount: 68 }
  ];

  const demographicEffectiveness = [
    { ageGroup: '18-30', successRate: 97.8, complications: 1.5 },
    { ageGroup: '31-45', successRate: 96.5, complications: 2.1 },
    { ageGroup: '46-60', successRate: 94.8, complications: 3.2 },
    { ageGroup: '61-75', successRate: 92.3, complications: 4.5 },
    { ageGroup: '76+', successRate: 89.7, complications: 6.2 }
  ];

  const experienceEffectiveness = [
    { experience: '0-5 years', successRate: 92.5, efficiency: 85, satisfaction: 4.6 },
    { experience: '6-10 years', successRate: 95.2, efficiency: 90, satisfaction: 4.8 },
    { experience: '11-15 years', successRate: 96.8, efficiency: 93, satisfaction: 4.9 },
    { experience: '16-20 years', successRate: 97.5, efficiency: 95, satisfaction: 4.9 },
    { experience: '20+ years', successRate: 96.9, efficiency: 94, satisfaction: 4.8 }
  ];

  const keyMetrics = [
    {
      label: 'Avg Success Rate',
      value: '95.0%',
      icon: 'Target',
      color: 'bg-success/10 text-success'
    },
    {
      label: 'Avg Duration',
      value: '79 min',
      icon: 'Clock',
      color: 'bg-primary/10 text-primary'
    },
    {
      label: 'Total Procedures',
      value: '857',
      icon: 'Activity',
      color: 'bg-warning/10 text-warning'
    },
    {
      label: 'Effectiveness Score',
      value: '93.2',
      icon: 'TrendingUp',
      color: 'bg-muted text-muted-foreground'
    }
  ];

  const renderChart = () => {
    switch (viewMode) {
      case 'procedure':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={procedureEffectiveness} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis
                dataKey="procedure"
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '11px' }}
                angle={-15}
                textAnchor="end"
                height={60}
              />
              <YAxis
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '11px' }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'var(--color-card)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                  color: 'var(--color-foreground)'
                }}
              />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              <Bar dataKey="successRate" fill="#059669" radius={[8, 8, 0, 0]} name="Success Rate (%)" />
            </BarChart>
          </ResponsiveContainer>
        );
      case 'demographics':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={demographicEffectiveness} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis
                dataKey="ageGroup"
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '11px' }}
              />
              <YAxis
                yAxisId="left"
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '11px' }}
                domain={[85, 100]}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '11px' }}
                domain={[0, 8]}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'var(--color-card)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                  color: 'var(--color-foreground)'
                }}
              />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="successRate"
                stroke="#2563EB"
                strokeWidth={2}
                name="Success Rate (%)"
                dot={{ fill: '#2563EB', r: 4 }}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="complications"
                stroke="#DC2626"
                strokeWidth={2}
                name="Complication Rate (%)"
                dot={{ fill: '#DC2626', r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        );
      case 'experience':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={experienceEffectiveness} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis
                dataKey="experience"
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '11px' }}
                angle={-15}
                textAnchor="end"
                height={60}
              />
              <YAxis
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '11px' }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'var(--color-card)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                  color: 'var(--color-foreground)'
                }}
              />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              <Bar dataKey="successRate" fill="#2563EB" name="Success Rate (%)" />
              <Bar dataKey="efficiency" fill="#059669" name="Efficiency (%)" />
            </BarChart>
          </ResponsiveContainer>
        );
      default:
        return null;
    }
  };

  return (
    <div className="bg-card rounded-xl border border-border shadow-elevation-2 p-4 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg md:text-xl font-semibold text-foreground flex items-center gap-2">
          <Icon name="TrendingUp" size={20} className="text-primary" />
          Treatment Effectiveness
        </h2>
        <span className="text-xs text-muted-foreground">
          {dateRange === 'day' ? 'Today' : dateRange === 'week' ? 'This Week' : dateRange === 'month' ? 'This Month' : dateRange === 'quarter' ? 'This Quarter' : 'This Year'}
        </span>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {keyMetrics?.map((metric, index) => (
          <div key={index} className="bg-muted/50 rounded-lg p-3 border border-border">
            <div className={`flex items-center justify-center w-8 h-8 rounded-lg ${metric?.color} mb-2`}>
              <Icon name={metric?.icon} size={16} />
            </div>
            <p className="text-xl font-bold text-foreground mb-1">{metric?.value}</p>
            <p className="text-xs text-muted-foreground">{metric?.label}</p>
          </div>
        ))}
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-foreground mb-2">View By</label>
        <div className="flex gap-2">
          {[
            { value: 'procedure', label: 'Procedure Type' },
            { value: 'demographics', label: 'Patient Demographics' },
            { value: 'experience', label: 'Provider Experience' }
          ]?.map((option) => (
            <button
              key={option?.value}
              onClick={() => setViewMode(option?.value)}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                viewMode === option?.value
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              {option?.label}
            </button>
          ))}
        </div>
      </div>

      <div className="h-80">{renderChart()}</div>

      <div className="mt-6 pt-6 border-t border-border">
        <h3 className="text-sm font-semibold text-foreground mb-3">Detailed Analysis</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="bg-success/10 rounded-lg p-3 border border-success/20">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="CheckCircle2" size={16} className="text-success" />
              <span className="text-xs font-semibold text-success">Highest Success</span>
            </div>
            <p className="text-sm text-foreground">
              {viewMode === 'procedure' && 'Preventive procedures: 98.5%'}
              {viewMode === 'demographics' && 'Age 18-30: 97.8% success rate'}
              {viewMode === 'experience' && '16-20 years: 97.5% success rate'}
            </p>
          </div>
          <div className="bg-primary/10 rounded-lg p-3 border border-primary/20">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Activity" size={16} className="text-primary" />
              <span className="text-xs font-semibold text-primary">Most Common</span>
            </div>
            <p className="text-sm text-foreground">
              {viewMode === 'procedure' && 'Preventive: 245 procedures'}
              {viewMode === 'demographics' && 'Age 31-45: Largest patient group'}
              {viewMode === 'experience' && '11-15 years: Optimal performance'}
            </p>
          </div>
          <div className="bg-warning/10 rounded-lg p-3 border border-warning/20">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="AlertCircle" size={16} className="text-warning" />
              <span className="text-xs font-semibold text-warning">Focus Area</span>
            </div>
            <p className="text-sm text-foreground">
              {viewMode === 'procedure' && 'Periodontal: Monitor outcomes'}
              {viewMode === 'demographics' && 'Age 76+: Enhanced care protocols'}
              {viewMode === 'experience' && '0-5 years: Mentorship programs'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TreatmentEffectivenessVisualization;